import Card from "../ProductCard";
import Loading from "./Loading";
import Error from "./Error";
import { Item } from "@/typings/items";
import useProductsQuery from "@/hooks/useProductsQuery";
import { SearchErrors } from "@/typings/ErrorStates";
import { dispatchCustomEvent } from "@/helpers/triggerAnalyticsEvent";
import { useEffect } from "react";

function getQueryParam(name: string, url?: string) {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, "\\$&");
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
  results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

function  setURLParams(input: string) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete("q");
  history.pushState(
    null,
    "",
    `${window.location.pathname}?q=${encodeURIComponent(input)}`
  );
}

const ProductsGrid = ({
  prompt,
  reload,
  handlePrompt = () => {},
  onSearchSubmit = () => {},
} 
: {
  prompt:string;
  reload:number;
  handlePrompt?: (query: string) => void;
  onSearchSubmit?: (query: boolean) => void;
}) => {

  const searchTerm = getQueryParam("q");
  const { response, loading } = useProductsQuery(searchTerm, reload);
  const handleScrollToSearch = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    const searchId =
      window.innerWidth <= 768 ? "mobile-search-bar" : "search-bar";

    document.getElementById(searchId)?.focus();

    dispatchCustomEvent({
      dataObject: "body-footer",
      dataType: "button",
      dataReason: "Joyhunt_back_to_search",
    });
  };

  const handleMarketplaceExit = () => {
    dispatchCustomEvent({
      dataObject: "body-footer",
      dataType: "button",
      dataReason: "Joyhunt_exit_to_marketplace_offers",
    });
    const url =
      "https://www.synchrony.com/marketplace/brands";
    window.open(url, "_blank");
  };

  if (response?.error) {
    return <Error error={response?.error} handlePrompt={handlePrompt} onSearchSubmit={onSearchSubmit} />;
  }

  if (loading) {
    return <Loading />;
  }

  if (!response && !loading) {
    return <Error error={SearchErrors.Unknown} handlePrompt={handlePrompt} onSearchSubmit={onSearchSubmit} />;
  }

  return (
    <div className="flex flex-col">
      <ul className="max-w-[1440px] grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mt-[24px] mx-auto mb-[48px]">
        {response?.data?.map((item: Item) => {
          return <Card item={item} key={item.productId} />;
        })}
      </ul>
      <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left gap-8 md:gap-4 w-full max-w-[1440px] px-[40px] mx-auto text-[16px] leading-[24px]">
        <div className="flex flex-col md:flex-row gap-2">
          <span>Not finding a product you're looking for?</span>{" "}
          <button
            className="flex items-center md:gap-1 font-semibold text-syfGreen syfclickevent"
            tabIndex={0}
            onClick={handleMarketplaceExit}
            data-object="body-footer"
            data-type="button"
            data-reason="Joyhunt_exit_to_marketplace_offers"
          >
            <span>Browse Partner Brand Financing Offers</span>{" "}
            <svg
              width="13"
              height="12"
              viewBox="0 0 13 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="icon-external-link">
                <path
                  id="Vector"
                  d="M9.5 6.5V9.5C9.5 9.76522 9.39464 10.0196 9.20711 10.2071C9.01957 10.3946 8.76522 10.5 8.5 10.5H3C2.73478 10.5 2.48043 10.3946 2.29289 10.2071C2.10536 10.0196 2 9.76522 2 9.5V4C2 3.73478 2.10536 3.48043 2.29289 3.29289C2.48043 3.10536 2.73478 3 3 3H6"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5px"
                />
                <path
                  id="Vector_2"
                  d="M8 1.5H11V4.5"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5px"
                />
                <path
                  id="Vector_3"
                  d="M5.5 7L11 1.5"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5px"
                />
              </g>
            </svg>
          </button>
        </div>

        <button
          onClick={handleScrollToSearch}
          className="syfclickevent text-syfGreen font-bold flex gap-1 items-start hover:shadow-[0px_4px_32px_0px_#00000014] p-3 rounded-[8px] hover:animate-bgFade"
          id="return-top"
          tabIndex={0}
          data-object="body-footer"
          data-type="button"
          data-reason="Joyhunt_return_to_top"
        >
          <span>Back to Search</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mt-1 ml-1"
            aria-hidden="true"
          >
            <path
              d="M1.1875 5.75L6.4375 0.25C6.59375 0.09375 6.78125 0.03125 7 0.03125C7.1875 0.03125 7.375 0.09375 7.53125 0.25L12.7812 5.75C13.0625 6.0625 13.0625 6.53125 12.75 6.8125C12.4688 7.09375 11.9688 7.09375 11.6875 6.78125L7.75 2.625V13.25C7.75 13.6875 7.40625 14 7 14C6.625 14 6.25 13.6875 6.25 13.25V2.625L2.28125 6.78125C2 7.09375 1.5 7.09375 1.21875 6.8125C0.90625 6.53125 0.90625 6.03125 1.1875 5.75Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ProductsGrid;
